import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import { noteModule } from "@rentiohq/shared-frontend/dist/reduxV2/note";
import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { formatPhone } from "@rentiohq/shared-frontend/dist/utils/phone/phone.utils";
import { Button, TextField } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { isCandidacySubmission } from "utils/application.utils";
import { Rating } from "../../../../../components/Rating";
import { ApplicationInfoIndicators } from "../../ApplicationsRentmatch/components/ApplicationTable/applicationIndicators";
import { getTotalIncome } from "../../ApplicationsRentmatch/components/DragItem/dragItem.utils";
import { pickVisitNote } from "../../ApplicationsRentmatch/components/SendToOwnerModal/sendToOwnerNote.utils";
import { WarningTooltip } from "../../ApplicationsRentmatch/components/WarningTooltip";
import * as S from "../VisitMode.styled";
import {
  formatVisitNote,
  getLabelsTitle,
  parseVisitNote,
} from "../visitMode.utils";
import { EmailIcon, PdfIcon, PhoneIcon, ProfileIcon } from "./VisitIcons";
import { VisitLabels } from "./VisitLabels";

/** Stars are tapped with a thumb here, not clicked with a mouse. */
const RATING_SIZE = 28;

/** A mark alone in a square of its own, rather than one beside a line. */
const PROFILE_ICON_SIZE = 20;

interface IProps {
  application: IApplication;
  isOpen: boolean;
  onToggle: () => void;
  /** True once the candidacy sits on the candidate-tenant step or beyond. */
  isInvited: boolean;
  onInvite: () => void;
  /** Asks for the candidacies again, whose own copy of the note drives the
   * handled state of a folded card. */
  onNoteChanged: () => void;
}

/**
 * How far back the card looks for the candidacy's own note.
 *
 * More than one, because the notes before it may be records of what an
 * owner was shown; small, because this runs for every visitor whose card
 * is opened during a day of viewings.
 */
const VISIT_NOTE_LOOKUP_LIMIT = 20;

export const VisitCard = (props: IProps) => {
  const { application, isOpen, onToggle, isInvited, onInvite, onNoteChanged } =
    props;
  const dispatch = useDispatch();
  const params = useParams<{ propertyId: string }>();

  const contact = application.contacts?.[0];
  const name = contact ? getName(contact) : "";
  const hasNoShow = !!application.noShowAt;
  // Ruled out on the dashboard. Nothing on this card said so, and the invite
  // button stayed, so a candidate could be asked for a rental profile minutes
  // after being turned down.
  const isNotEligible = application.step === EIntakeStep.notEligible;
  const notePath = `/applications/${application.id}/notes`;

  // The feedback field is the note that already hangs off this candidacy, the
  // same one the candidate list shows behind its warning triangle.
  //
  // Fetched only once the card is opened, and the way to not fetch is to pass
  // no query at all: usePaged has no disabled flag and returns early on a
  // missing query. Without that a day with a dozen visitors would fire a dozen
  // note requests the moment the screen loads, for notes nobody is reading.
  const { items: notes, refetch: refetchNotes } = noteModule.hooks.usePaged({
    shouldRefetch: isOpen,
    query: isOpen
      ? {
          page: 1,
          limit: VISIT_NOTE_LOOKUP_LIMIT,
          // Oldest first, said out loud rather than left to the API's own
          // order, because this is the note the card has always edited and
          // nothing may move under a broker mid-visit.
          sort: [{ field: "createdAt", method: "ASC" }],
        }
      : undefined,
    customPath: notePath,
  });
  // Not simply the first one: a candidacy shared with an owner before anyone
  // wrote about it has that record as its only note, and clearing the
  // feedback field deletes whatever note this card is editing (DEV-3326).
  const note = pickVisitNote(notes);

  const [draft, setDraft] = React.useState("");
  const [labels, setLabels] = React.useState<string[]>([]);
  const [isSavingNote, setIsSavingNote] = React.useState(false);

  /**
   * What the server last gave us for this candidacy.
   *
   * The fetch above runs only while the card is open, so by the time the card
   * folds it has nothing left to hand back. Reading the note off it there made
   * every close look like the first note ever written for this person, and
   * wrote one: a fresh note on every open and close, saying nothing new.
   */
  const savedRef = React.useRef<{
    id?: NonNullable<typeof note>["id"];
    content: string;
  }>({
    content: "",
  });

  React.useEffect(() => {
    if (isOpen) {
      const parsed = parseVisitNote(note?.content);

      setDraft(parsed.text);
      setLabels(parsed.labels);
      savedRef.current = { id: note?.id, content: note?.content || "" };
    }
  }, [isOpen, note?.id, note?.content]);

  // Any one of the three counts as recorded, so the edge stops asking for
  // attention as soon as the broker has said anything at all about this person.
  //
  // Feedback is read off the application rather than off the note fetch above,
  // because that fetch only runs while the card is open and a closed card must
  // show the right edge too. It is the same field the candidate list uses.
  const noteContent = isOpen
    ? note?.content
    : application.otherApplicationsMeta?.internalNote;
  const hasFeedback = !!noteContent;
  // Handled once anything at all has been said about this person, and certainly
  // once they have been invited: that is where this screen's work ends.
  const isDone =
    hasNoShow ||
    isNotEligible ||
    application.rating != null ||
    hasFeedback ||
    isInvited;

  // Invited and having applied are two different things, and the broker on the
  // doorstep cares which: one still has to send their profile in.
  const hasSubmitted = isCandidacySubmission(application.submittedStep);

  /** The two letters standing in for a photo we do not have. */
  const initials = [contact?.firstname?.[0], contact?.lastname?.[0]]
    .filter(Boolean)
    .join("");

  const handleRatingChange = (value: number) => {
    dispatch(
      applicationActions.setApplicationRatingStart.getAction({
        id: application.id,
        value,
        onSuccess: onNoteChanged,
      }),
    );
  };

  const handleNoShowToggle = () => {
    dispatch(
      applicationActions.setApplicationNoShowStart.getAction({
        id: application.id,
        value: !hasNoShow,
        onSuccess: onNoteChanged,
      }),
    );
  };

  const handleSaveNote = () => {
    const content = formatVisitNote(labels, draft);
    const saved = savedRef.current;

    // Nothing typed and nothing there: a blur on an untouched field, or a card
    // folding on one.
    if (content === saved.content) {
      return;
    }

    setIsSavingNote(true);

    const onSettled = () => {
      setIsSavingNote(false);
      refetchNotes();
      onNoteChanged();
    };

    // Taken as written before the server says so, because a blur and a fold can
    // follow each other closely enough that the second would otherwise write
    // the same note a second time.
    savedRef.current = { id: content ? saved.id : undefined, content };

    if (!content && saved.id) {
      // Emptying the field is how a note is taken back, so it has to delete the
      // note rather than save an empty one. Without this the field cleared on
      // screen and the old text came straight back on the next read.
      dispatch(
        noteModule.actions.removeStart.getAction({
          id: saved.id,
          customPath: notePath,
          onSuccess: onSettled,
          onFailure: onSettled,
        }),
      );

      return;
    }

    dispatch(
      saved.id
        ? noteModule.actions.updateStart.getAction({
            id: saved.id,
            data: { content },
            onSuccess: onSettled,
            onFailure: onSettled,
          })
        : noteModule.actions.createStart.getAction({
            data: { content },
            customPath: notePath,
            onSuccess: onSettled,
            onFailure: onSettled,
          }),
    );
  };

  // Tapping five labels is one note being written, not five. The save is held
  // until the card folds, and a ref carries it there because an effect would
  // otherwise keep the closure from the render it was set up in.
  const saveRef = React.useRef(handleSaveNote);
  saveRef.current = handleSaveNote;

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    return () => {
      saveRef.current();
    };
  }, [isOpen]);

  const renderSummaryRight = () => {
    if (isNotEligible) {
      return (
        <S.StatusLabel $variant="noShow">
          {getLocalizedText("property.dashboard.application.4.title")}
        </S.StatusLabel>
      );
    }

    if (hasNoShow) {
      return (
        <S.StatusLabel $variant="noShow">
          {getLocalizedText("application.action.no_show")}
        </S.StatusLabel>
      );
    }

    return (
      <>
        {isInvited && (
          <S.StatusLabel $variant="invited">
            {/* The same distinction the candidate list draws, spelled out: on a
                card there is no column header to read the bare check against. */}
            {hasSubmitted && (
              <svg
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ flexShrink: 0 }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            )}
            {getLocalizedText(
              hasSubmitted
                ? "rentmatch.applications.state.submitted"
                : "property.dashboard.application.invited_as_candidate",
            )}
          </S.StatusLabel>
        )}
      </>
    );
  };

  return (
    <S.Card $isDone={isDone} $isOpen={isOpen}>
      <S.CardSummary onClick={onToggle} type="button">
        <S.Avatar $isDone={isDone}>{initials || name.slice(0, 2)}</S.Avatar>
        <S.SummaryMain>
          <S.CandidateName title={name} $isDone={isDone}>
            {name}
          </S.CandidateName>
          <S.SummaryRight>{renderSummaryRight()}</S.SummaryRight>
        </S.SummaryMain>

        {/* Score, warning and chevron sit together at the end of the row, so
            they line up with each other rather than with whichever line of the
            name and its label happens to be first. */}
        <S.SummaryEnd>
          {!hasNoShow && application.rating != null && !isOpen && (
            <S.ScoreBadge>{application.rating}</S.ScoreBadge>
          )}
          {/* The same triangle the candidate list raises, carrying the same
              two things in one tooltip: a no-show and a note. */}
          {!isOpen && (hasNoShow || hasFeedback) && (
            <WarningTooltip
              tooltipContent={
                <>
                  {hasNoShow && (
                    <div>
                      <strong>
                        {getLocalizedText("application.action.no_show")}
                      </strong>
                      <br />
                      {formatDate(
                        new Date(application.noShowAt as any),
                        "d MMMM yyyy",
                      )}
                    </div>
                  )}
                  {hasNoShow && hasFeedback && <br />}
                  {hasFeedback && (
                    <div style={{ fontStyle: "italic" }}>
                      &quot;{noteContent}&quot;
                    </div>
                  )}
                </>
              }
            />
          )}
          {/* Says the card opens, and which way it is standing. */}
          <S.Chevron $isOpen={isOpen}>
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="1 1 6 6 11 1" />
            </svg>
          </S.Chevron>
        </S.SummaryEnd>
      </S.CardSummary>

      {isOpen && (
        <S.CardBody>
          <S.Section $isShaded>
            {/* High up on purpose: the reason to reach for a phone number is
              that somebody is not standing in front of you. */}
            <S.Facts>
              {/* Every contact on the candidacy, as the overview counts it: a
                couple's incomes add up, and only the first one would understate
                what they can carry. */}
              <S.Income>
                {formatCurrency(getTotalIncome(application.contacts))}
              </S.Income>
              {/* The overview's own component, so the two cannot come to show
                different facts about the same candidacy. */}
              <ApplicationInfoIndicators application={application} />
            </S.Facts>
          </S.Section>

          <S.Section>
            <S.ContactRow>
              <S.ContactLinks>
                {contact?.phone && (
                  <S.ContactLink href={`tel:${contact.phone}`}>
                    <S.IconBox>
                      <PhoneIcon />
                    </S.IconBox>
                    {formatPhone(contact.phone)}
                  </S.ContactLink>
                )}
                {contact?.email && (
                  <S.ContactLink href={`mailto:${contact.email}`}>
                    <S.IconBox>
                      <EmailIcon />
                    </S.IconBox>
                    {contact.email}
                  </S.ContactLink>
                )}
              </S.ContactLinks>

              {/* Both open a tab of their own: this screen is being worked
                through, and losing your place in it to read a profile would
                cost more than the tab does. */}
              <S.ProfileLinks>
                {application.pdfUrl && (
                  <S.ProfileLink
                    href={application.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={getLocalizedText("application.pdf.status.generated")}
                  >
                    <S.IconBox>
                      <PdfIcon size={PROFILE_ICON_SIZE} />
                    </S.IconBox>
                  </S.ProfileLink>
                )}
                {params.propertyId && (
                  <S.ProfileLink
                    href={`/properties/${params.propertyId}/applications?applicationId=${application.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={getLocalizedText("view.application")}
                  >
                    <S.IconBox>
                      <ProfileIcon size={PROFILE_ICON_SIZE} />
                    </S.IconBox>
                  </S.ProfileLink>
                )}
              </S.ProfileLinks>
            </S.ContactRow>
          </S.Section>

          <S.Section>
            <div>
              <S.FieldLabel>{getLocalizedText("property.rating")}</S.FieldLabel>
              <Rating
                value={application.rating ?? 0}
                size={RATING_SIZE}
                onChange={handleRatingChange}
              />
            </div>

            <div>
              <S.FieldLabel>{getLabelsTitle()}</S.FieldLabel>
              <VisitLabels value={labels} onChange={setLabels} />
            </div>

            <div>
              <S.FieldLabel>
                {getLocalizedText("visit_mode.card.feedback")}
              </S.FieldLabel>
              <S.NoteField>
                <TextField
                  name={`feedback-${application.id}`}
                  value={draft}
                  elementType="textarea"
                  multiline
                  rows={3}
                  placeholder={getLocalizedText(
                    "visit_mode.card.feedback.placeholder",
                  )}
                  onChange={(e: any) => setDraft(e.target.value)}
                  onBlur={handleSaveNote}
                  disabled={isSavingNote}
                />
              </S.NoteField>
            </div>

            {/* Hidden rather than disabled: somebody who never turned up cannot be
              asked for their rental profile, and somebody already ruled out
              should not be asked again by accident. */}
            {!hasNoShow && !isInvited && !isNotEligible && (
              <Button appearance="primary" onClick={onInvite}>
                {getLocalizedText("visit_mode.card.invite")}
              </Button>
            )}

            <S.NoShowButton onClick={handleNoShowToggle} type="button">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
                <path d="M17 2h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4" />
              </svg>
              {getLocalizedText(
                hasNoShow
                  ? "application.action.remove_no_show"
                  : "application.action.no_show",
              )}
            </S.NoShowButton>
          </S.Section>
        </S.CardBody>
      )}
    </S.Card>
  );
};
